import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { publicApi, formatFecha, formatHora } from "@/lib/agenda";

export default function ConfirmPage() {
  const { id } = useParams();
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const token = search ? (search.get("t") || "") : "";
  const [appt, setAppt] = useState(undefined); // undefined = cargando, null = no existe
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    publicApi
      .getAppointment(id, token)
      .then((d) => {
        setAppt(d);
        setConfirmed(!!d.reminder_confirmed);
      })
      .catch(() => setAppt(null));
  }, [id, token]);

  const confirm = async () => {
    setLoading(true);
    setError(false);
    try {
      await publicApi.confirm(id, token);
      setConfirmed(true);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 warm-canvas" data-testid="confirm-page">
      <div className="w-full max-w-sm editorial-card p-8 text-center animate-fade-up">
        <span className="font-serif-brand font-extrabold text-lg tracking-tight block mb-5" style={{ color: "#2E2A8F" }}>
          Agenda Terapias
        </span>
        <span className="flex items-center justify-center h-11 w-11 rounded-2xl text-white mx-auto" style={{ backgroundColor: "#275FBE" }}>
          <CalendarDays className="h-6 w-6" />
        </span>

        {appt === undefined ? (
          <p className="mt-6 text-slate-400">Un momento</p>
        ) : appt === null ? (
          <>
            <h1 className="font-heading text-xl font-bold text-slate-900 mt-6">No encontramos tu cita</h1>
            <p className="text-sm text-slate-500 mt-2">El enlace no es válido o la cita ya no existe.</p>
          </>
        ) : confirmed ? (
          <div data-testid="confirm-done">
            <span className="flex items-center justify-center h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 mx-auto mt-6">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="font-heading text-xl font-bold text-slate-900 mt-4">Cita confirmada</h1>
            <p className="text-sm text-slate-600 mt-2">
              Gracias, {appt.client_name}. Te esperamos.
            </p>
            <p className="inline-flex items-center gap-1.5 text-sm text-slate-500 mt-3">
              <Clock className="h-4 w-4" />
              {formatFecha(appt.appointment_datetime)} · {formatHora(appt.appointment_datetime)}
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-xl font-bold text-slate-900 mt-6">Hola, {appt.client_name}</h1>
            <p className="text-sm text-slate-600 mt-2">¿Confirmas tu cita?</p>
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 mt-3">
              <Clock className="h-4 w-4" />
              {formatFecha(appt.appointment_datetime)} · {formatHora(appt.appointment_datetime)}
            </p>
            {error && <p className="text-sm text-red-600 mt-3">Algo no ha ido bien. Prueba otra vez o vuelve más tarde.</p>}
            <Button
              onClick={confirm}
              disabled={loading}
              className="w-full rounded-full text-white py-6 mt-6"
              style={{ backgroundColor: "#275FBE" }}
              data-testid="confirm-button"
            >
              {loading ? "Un momento" : "Sí, confirmo"}
            </Button>
          </>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-6">Agenda Terapias · Tu trabajo, más claro y sin olvidos.</p>
    </div>
  );
}
